import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Stack, Select, MenuItem, FormControl, Chip, IconButton, Tooltip,
    Checkbox, FormControlLabel, CircularProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import http, { apiErrorMessage } from '../Api/http';
import { GetCreatePermissions, SetCreatePermission } from '../Api/Api';
import { selectEntities, selectActiveEntityId } from '../redux/slices/orgSlice';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER, INK, MUTED } from '../theme';
import { card, field, tonalBtn, SkeletonRows, EmptyState } from '../components/uiKit';

export default function ChatSettingsPage() {
    const entities = useSelector(selectEntities);
    const activeEntityId = useSelector(selectActiveEntityId);

    const [selectedEntityId, setSelectedEntityId] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [savingId, setSavingId] = useState(null);
    const [applyAll, setApplyAll] = useState({});

    // Default to whatever entity is active in the top-bar switcher, but only
    // once — after that this page's own selector is the source of truth and
    // is deliberately decoupled from the top-bar (see load(), below).
    useEffect(() => {
        if (selectedEntityId != null || entities.length === 0) return;
        const fallback = entities.some((e) => e.id === activeEntityId) ? activeEntityId : entities[0].id;
        setSelectedEntityId(fallback);
    }, [entities, activeEntityId, selectedEntityId]);

    const load = useCallback(async () => {
        if (selectedEntityId == null) return;
        setLoading(true);
        setLoadError('');
        try {
            // GetCreatePermissions returns exactly one row per role for the
            // entity this PAGE has selected (the 3 system roles plus the
            // entity's custom roles), so the screen renders only those rows.
            // The entity goes in as the `entityId` query param rather than
            // relying on http.js's top-bar X-Entity-Id, since this screen
            // lets a MasterAdmin inspect a different entity without
            // switching their whole session over to it.
            const { data: permsBody } = await http.get(GetCreatePermissions, { params: { entityId: selectedEntityId } });
            if (permsBody?.error) throw new Error(permsBody.message || 'Could not load group chat settings.');
            const permRows = Array.isArray(permsBody?.data)
                ? permsBody.data
                : (Array.isArray(permsBody?.data?.items) ? permsBody.data.items : []);

            setPermissions(permRows);
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load group chat settings.'));
        } finally {
            setLoading(false);
        }
    }, [selectedEntityId]);

    useEffect(() => { load(); }, [load]);

    // The API already applies the defaults (MasterAdmin and Admin allowed,
    // every other role not) and flags them with isExplicitlySet: false, which
    // drives the "(default)" badge.
    const rows = useMemo(() => permissions.map((p) => ({
        id: p.userTypeId,
        name: p.userTypeName,
        isSystem: Boolean(p.isSystemRole),
        canCreateGroups: Boolean(p.canCreateGroups),
        isExplicitlySet: Boolean(p.isExplicitlySet),
    })), [permissions]);

    const handleChange = async (role, canCreate) => {
        // EntityId null applies the change to every entity at once, but
        // that's only meaningful for the system roles — an entity's own
        // custom role can only be set for that entity. Guard it here too,
        // not just in the UI, in case a stale checkbox state slips through.
        const wantsAll = role.isSystem && Boolean(applyAll[role.id]);

        setSavingId(role.id);
        try {
            const { data: body } = await http.put(SetCreatePermission, {
                EntityId: wantsAll ? null : selectedEntityId,
                UserTypeId: role.id,
                CanCreate: canCreate,
            });
            if (body?.error) throw new Error(body.message || 'Could not update the setting.');

            toast.success(
                `${role.name} ${canCreate ? 'can now create' : 'can no longer create'} groups`
                + (wantsAll ? ' in every entity.' : '.'),
            );
            // Re-fetch rather than trust a purely-local optimistic merge — a
            // "wantsAll" write can also change OTHER rows the next load()
            // brings back into view once this entity's own row is confirmed.
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not update the setting.'));
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ pb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexWrap: 'wrap', gap: 1.5 }}>
                <Stack direction="row" spacing={1.6} sx={{ alignItems: 'center' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <TuneRoundedIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 19, fontWeight: 800, color: INK, letterSpacing: '-0.3px' }}>Group Chat Settings</Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.2 }}>Control who can see "New Group" in Chat</Typography>
                    </Box>
                </Stack>
                <Tooltip arrow title="Reload from server">
                    <span>
                        <IconButton onClick={load} disabled={loading || selectedEntityId == null} sx={{ width: 42, height: 42, border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', color: '#5B6472', '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                            <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Box sx={{ ...card, p: 1.8, mb: 1.8, display: 'flex', gap: 1.2, alignItems: 'flex-start', bgcolor: '#F8FAFC' }}>
                <InfoRoundedIcon sx={{ fontSize: 19, color: PRIMARY, flexShrink: 0, mt: 0.1 }} />
                <Typography sx={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                    Controls which roles see the "New Group" option in Chat. MasterAdmin and Admin are
                    allowed by default; every other role is not, until you set it explicitly here. A change
                    made with "Apply to every entity" checked (system roles only) takes effect
                    for every business entity at once, not just the one selected below.
                </Typography>
            </Box>

            <Box sx={{ ...card, p: 1.8, mb: 1.8 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.8 }}>Business entity</Typography>
                <FormControl size="small" sx={{ ...field, minWidth: 260 }}>
                    <Select
                        value={selectedEntityId ?? ''}
                        displayEmpty
                        onChange={(e) => setSelectedEntityId(e.target.value)}
                        disabled={entities.length === 0}
                    >
                        {entities.length === 0 && <MenuItem value="" disabled>No entities available</MenuItem>}
                        {entities.map((e) => (
                            <MenuItem key={e.id} value={e.id}>{e.name}{e.code ? ` (${e.code})` : ''}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {loadError && !loading && (
                <Box sx={{ ...card, p: 4, textAlign: 'center' }}>
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 38, color: '#F87171' }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK, mt: 1 }}>Couldn't load group chat settings</Typography>
                    <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.5 }}>{loadError}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Box component="button" onClick={load} sx={{ ...tonalBtn, height: 40, px: 2.4, border: 'none', cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
                            <RefreshRoundedIcon sx={{ fontSize: 17 }} /> Try again
                        </Box>
                    </Box>
                </Box>
            )}

            {loading && !loadError && (
                <Box sx={{ ...card, p: 0 }}><SkeletonRows count={4} height={72} /></Box>
            )}

            {!loading && !loadError && rows.length === 0 && (
                <Box sx={{ ...card }}>
                    <EmptyState icon={GroupsRoundedIcon} title="No roles found for this entity" hint="Create a role first from Roles & Access." />
                </Box>
            )}

            {!loading && !loadError && rows.length > 0 && (
                <Stack spacing={1.2}>
                    {rows.map((r) => (
                        <Box key={r.id} sx={{ ...card, p: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.6 }}>
                            <Box sx={{ minWidth: 160, flex: 1 }}>
                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: INK }}>{r.name}</Typography>
                                    <Chip
                                        size="small"
                                        icon={r.isSystem ? <LockRoundedIcon sx={{ fontSize: '13px !important' }} /> : undefined}
                                        label={r.isSystem ? 'System' : 'Custom'}
                                        sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: r.isSystem ? '#E4F0F9' : '#FEF3C7', color: r.isSystem ? '#135F93' : '#B45309', '& .MuiChip-icon': { color: '#135F93', ml: 0.6 } }}
                                    />
                                    {!r.isExplicitlySet && (
                                        <Chip size="small" label="(default)" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                                    )}
                                </Stack>
                            </Box>

                            {r.isSystem && (
                                <FormControlLabel
                                    sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 12, color: MUTED } }}
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(applyAll[r.id])}
                                            disabled={savingId === r.id}
                                            onChange={(e) => setApplyAll((m) => ({ ...m, [r.id]: e.target.checked }))}
                                        />
                                    )}
                                    label="Apply to every entity"
                                />
                            )}

                            <FormControl size="small" sx={{ ...field, minWidth: 150 }}>
                                <Select
                                    value={r.canCreateGroups ? 'allowed' : 'not_allowed'}
                                    disabled={savingId === r.id}
                                    onChange={(e) => handleChange(r, e.target.value === 'allowed')}
                                    endAdornment={savingId === r.id ? <CircularProgress size={14} thickness={5} sx={{ color: PRIMARY, mr: 2 }} /> : undefined}
                                >
                                    <MenuItem value="allowed">Allowed</MenuItem>
                                    <MenuItem value="not_allowed">Not allowed</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
